/**
 * Company: SPHMedia
 * Description: [Kaki Makan Sub Section List Component]
 */

import {
  KakiMakanSubSectionListMapping,
  VIEW_LESS_COPY,
  VIEW_MORE_COPY,
} from "@config/kakiMakan";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import useWindowDimensions from "@hooks/useWindowDimensions";
import { List, ListItem } from "@sphtech/sph-web-ui/elements/List";
import { NoSSR } from "@sphtech/web2-core/components";
import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { ReactElement, useMemo } from "react";

import useKakiMakanSubSectionList from "./hooks/useKakiMakanSubSectionList";

export type KakiMakanSubSectionListProps = {
  onSelect: (section: string | null) => void;
  initialSelection?: string | null;
  initialExpanded?: boolean;
};

const VISIBLE_COUNT = 5; // number of items visible on desktop without expanding

// Framer Motion variants for the container and items
// Used to stagger the animation of extra items when they appear/disappear
const containerVariants = {
  show: {
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
  hide: {
    transition: { staggerChildren: 0.08, staggerDirection: -1 },
  },
};

// Item variants for individual buttons
const itemVariants = {
  hidden: { opacity: 0, x: 12, scale: 0.98 },
  show: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.25 } },
  hide: { opacity: 0, x: 12, scale: 0.98, transition: { duration: 0.2 } },
};

/**
 * KakiMakanSubSectionList
 * Description: [Displays a list of sub-sections for Kaki Makan]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function KakiMakanSubSectionList(
  props: KakiMakanSubSectionListProps,
): ReactElement {
  const bhCustomEvent = useBHCustomEventAnalyticsCall();
  const { expanded, selectedSection, handleSelect, handleToggle } =
    useKakiMakanSubSectionList({ bhCustomEvent, ...props });

  const { width } = useWindowDimensions();
  // Tailwind's `lg` breakpoint is 992px by default — consider that desktop
  const isDesktop = useMemo(() => width >= 992, [width]);

  const items = Object.keys(KakiMakanSubSectionListMapping);

  const buttonStyles =
    "w-max text-xs font-semimedium text-text-body leading-[150%] border border-solid border-border-strong rounded-[50px] px-2 pb-0.5 pt-0";

  return (
    <div className="w-full flex justify-center py-0.5">
      <List
        testId="kaki-makan-sub-section-filter"
        className={cx(
          "flex items-start gap-2 pb-2.5 lg:pb-0",
          // Mobile: horizontal scroll
          "overflow-x-auto lg:overflow-visible",
          // Desktop: fixed width with wrapping when expanded
          "max-w-[650px] w-full",
          expanded ? "flex-wrap" : "flex-nowrap",
        )}
      >
        <>
          {/* 1) Render the always-visible group (first VISIBLE_COUNT items) */}
          {items.slice(0, VISIBLE_COUNT).map((item, index) => (
            <div
              key={index}
              className="cursor-pointer flex items-center justify-center"
            >
              <ListItem
                testId={`sub-section-nav-item-${index}`}
                className="cursor-pointer flex items-center justify-center"
              >
                <button
                  data-testid={`sub-section-button-${item}`}
                  onClick={() => {
                    handleSelect(KakiMakanSubSectionListMapping[item]);
                  }}
                  className={cx(
                    buttonStyles,
                    selectedSection === KakiMakanSubSectionListMapping[item]
                      ? "bg-bh-bg-blue-pale"
                      : "bg-bg-primary",
                  )}
                >
                  {item}
                </button>
              </ListItem>
            </div>
          ))}

          {/* 2) Render extra items as a single animated block inserted after the visible items.
              We only render this block if either: mobile (isDesktop === false) OR expanded === true.
              AnimatePresence + containerVariants + itemVariants give us a staggered enter/exit.
        */}
          {items.length > VISIBLE_COUNT && (
            <AnimatePresence mode="sync">
              <NoSSR>
                {(!isDesktop || expanded) && (
                  <motion.div
                    key="extra-items-container"
                    className="flex gap-2 lg:flex-wrap"
                    variants={containerVariants}
                    initial="hide"
                    animate="show"
                    exit="hide"
                    layout
                  >
                    {items.slice(VISIBLE_COUNT).map((item, idx) => {
                      const index = VISIBLE_COUNT + idx;
                      return (
                        <motion.div
                          key={index}
                          variants={itemVariants}
                          className="cursor-pointer flex items-center justify-center"
                          layout
                        >
                          <ListItem
                            testId={`sub-section-nav-item-${index}`}
                            className="cursor-pointer flex items-center justify-center"
                          >
                            <button
                              data-testid={`sub-section-button-${item}`}
                              onClick={() => {
                                handleSelect(
                                  KakiMakanSubSectionListMapping[item],
                                );
                              }}
                              className={cx(
                                buttonStyles,
                                selectedSection ===
                                  KakiMakanSubSectionListMapping[item]
                                  ? "bg-bh-bg-blue-pale"
                                  : "bg-bg-primary",
                              )}
                            >
                              {item}
                            </button>
                          </ListItem>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                )}
              </NoSSR>
            </AnimatePresence>
          )}

          {/* 3) View More / View Less — only render on desktop */}
          <NoSSR>
            {items.length > VISIBLE_COUNT && isDesktop && (
              <ListItem
                testId="list-expand-collapse"
                className="hidden lg:flex"
              >
                <button
                  data-testid="toggle-list"
                  onClick={handleToggle}
                  className={buttonStyles}
                >
                  {expanded ? VIEW_LESS_COPY : VIEW_MORE_COPY}
                </button>
              </ListItem>
            )}
          </NoSSR>
        </>
      </List>
    </div>
  );
}
